import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigationBlocker } from '../context/NavigationBlockerContext';
import InfoDialog from './InfoDialog';
import ConverterModal from './ConverterModal';

const MainNavigation: React.FC = () => {
    const { authState, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { 
        shouldBlockNavigation, 
        getBlockMessage, 
        isDialogOpen, 
        dialogTitle, 
        dialogMessage, 
        showDialog, 
        hideDialog,
        setPendingNavigation,
        executePendingNavigation
    } = useNavigationBlocker();
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isConvertersOpen, setIsConvertersOpen] = useState(false);
    const [isConverterModalOpen, setIsConverterModalOpen] = useState(false);
    const [selectedConverter, setSelectedConverter] = useState<'chanakya' | 'gujarati' | null>(null);
    const settingsRef = useRef<HTMLDivElement>(null);
    const convertersRef = useRef<HTMLDivElement>(null);
    const userRole = authState.user?.role;
    const userName = authState.user?.name;

    const canManageUsers = userRole === 'superadmin' || userRole === 'admin';
    const canSeeCMSDashboard = true; // All roles can access CMS Dashboard
    const canAccessAdmin = userRole === 'superadmin' || userRole === 'admin';
    const canAccessReview = userRole === 'reviewer' || userRole === 'editor-reviewer' || userRole === 'admin' || userRole === 'superadmin';
    const canChangePassword = userRole === 'reviewer' || userRole === 'editor' || userRole === 'editor-reviewer';

    // Handler for Continue Anyway button
    const handleContinueAnyway = () => {
        const pending = executePendingNavigation();
        if (pending) {
            if (pending.type === 'route' && pending.target) {
                navigate(pending.target);
            } else if (pending.type === 'logout') {
                logout();
            } else if (pending.type === 'password') {
                setIsSettingsOpen(false);
                navigate('/password');
            }
        }
    };

    // Custom NavLink wrapper that checks navigation blocking
    const BlockableNavLink: React.FC<{ to: string; className?: string | ((props: { isActive: boolean }) => string); children: React.ReactNode }> = ({ to, className, children }) => {
        const handleClick = (e: React.MouseEvent) => {
            if (shouldBlockNavigation && location.pathname !== to) {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                setPendingNavigation({ type: 'route', target: to });
                const message = getBlockMessage();
                showDialog('Save in Progress', message);
                return false;
            }
        };

        const classNameValue = typeof className === 'function' ? className({ isActive: location.pathname === to }) : className;

        return (
            <NavLink
                to={to}
                className={classNameValue}
                onClick={handleClick}
            >
                {children}
            </NavLink>
        );
    };

    // Close settings dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
                setIsSettingsOpen(false);
            }
            if (convertersRef.current && !convertersRef.current.contains(event.target as Node)) {
                setIsConvertersOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLogout = () => {
        if (shouldBlockNavigation) {
            setPendingNavigation({ type: 'logout' });
            showDialog('Unsaved Changes', getBlockMessage());
            return;
        }
        logout();
    };

    const handleSettingsClick = () => {
        setIsSettingsOpen(!isSettingsOpen);
    };

    const handleConvertersClick = () => {
        setIsConvertersOpen(!isConvertersOpen);
    };

    const handlePasswordChange = () => {
        if (shouldBlockNavigation) {
            setPendingNavigation({ type: 'password' });
            showDialog('Save in Progress', getBlockMessage());
            return;
        }
        setIsSettingsOpen(false);
        navigate('/password');
    };

    const handleConverterSelect = (converterType: 'chanakya' | 'gujarati') => {
        setIsConvertersOpen(false);
        setSelectedConverter(converterType);
        setIsConverterModalOpen(true);
    };

    const handleCloseConverterModal = () => {
        setIsConverterModalOpen(false);
        setSelectedConverter(null);
    };

    return (
        <>
            <nav className="main-navigation">
                <div className="nav-container">
                    <ul className="nav-tabs">
                        {canSeeCMSDashboard && (
                            <li>
                                <BlockableNavLink
                                    to="/cms/dashboard"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    Content Sources
                                </BlockableNavLink>
                            </li>
                        )}
                        {canAccessReview && (
                            <li>
                                <BlockableNavLink
                                    to="/cms/review"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    Review
                                </BlockableNavLink>
                            </li>
                        )}
                        {canAccessAdmin && (
                            <li>
                                <BlockableNavLink
                                    to="/cms/ai-extraction"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    AI Extraction
                                </BlockableNavLink>
                            </li>
                        )}
                        {canManageUsers && (
                            <li>
                                <BlockableNavLink
                                    to="/users"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    User Management
                                </BlockableNavLink>
                            </li>
                        )}
                        {canAccessAdmin && (
                            <li>
                                <BlockableNavLink
                                    to="/admin"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    Admin
                                </BlockableNavLink>
                            </li>
                        )}
                    </ul>
                    <div className="nav-user-section">
                        <span className="user-info">
                            <span className="user-name">{userName}</span>
                            <span className="user-role">({userRole})</span>
                        </span>
                        <div className="nav-actions">
                            <div className="content-cleanup-info">
                                <button 
                                    className="info-button"
                                    title="Content Cleanup Instructions"
                                >
                                    <Info size={20} />
                                </button>
                                <div className="content-cleanup-popover">
                                    <div className="popover-title">Content Cleanup Checklist</div>
                                    <ol className="popover-list">
                                        <li><strong>Define Hierarchy:</strong> Systematically divide the content into meaningful sections using the appropriate heading tags (H1, H2, H3, etc.).</li>
                                        <li><strong>Formatting:</strong> Convert special characters (like  ) into standard bullet points.</li>
                                        <li><strong>Lists:</strong> Ensure all existing lists are correctly formatted as standard bullet lists.</li>
                                        <li><strong>Title Page:</strong> Add a [Title Page] if one does not already exist.</li>
                                        <li><strong>Page Numbers:</strong> Insert page numbers in the bottom-left.</li>
                                        <li><strong>Spacing Cleanup:</strong> Remove extra line breaks and clean up spacing.</li>
                                        <li><strong>Images:</strong> Replace [Image: broken reference] with placeholders and add captions.</li>
                                        <li><strong>Annotations:</strong> Convert all inline notes into properly formatted annotations.</li>
                                    </ol>
                                </div>
                            </div>
                            <div className="converters-dropdown" ref={convertersRef}>
                                <button 
                                    className="converters-button"
                                    onClick={handleConvertersClick}
                                    title="Converters"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07M18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10M15.88 17L17.5 12.67L19.12 17H15.88Z"/>
                                    </svg>
                                </button>
                                {isConvertersOpen && (
                                    <div className="converters-menu">
                                        <button 
                                            className="converters-menu-item"
                                            onClick={() => handleConverterSelect('chanakya')}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07M18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10M15.88 17L17.5 12.67L19.12 17H15.88Z"/>
                                            </svg>
                                            Sanksrit/Hindi Converter
                                        </button>
                                        <button 
                                            className="converters-menu-item"
                                            onClick={() => handleConverterSelect('gujarati')}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07M18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10M15.88 17L17.5 12.67L19.12 17H15.88Z"/>
                                            </svg>
                                            Gujarati Converter
                                        </button>
                                    </div>
                                )}
                            </div>
                            {canChangePassword && (
                                <div className="settings-dropdown" ref={settingsRef}>
                                    <button 
                                        className="settings-button"
                                        onClick={handleSettingsClick}
                                        title="Settings"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5M19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"/>
                                        </svg>
                                    </button>
                                    {isSettingsOpen && (
                                        <div className="settings-menu">
                                            <button 
                                                className="settings-menu-item"
                                                onClick={handlePasswordChange}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 17A2 2 0 0 0 14 15C14 13.89 13.1 13 12 13A2 2 0 0 0 10 15A2 2 0 0 0 12 17M18 8A2 2 0 0 1 20 10V20A2 2 0 0 1 18 22H6A2 2 0 0 1 4 20V10C4 8.89 4.9 8 6 8H7V6A5 5 0 0 1 12 1A5 5 0 0 1 17 6V8H18M12 3A3 3 0 0 0 9 6V8H15V6A3 3 0 0 0 12 3Z"/>
                                                </svg>
                                                Change Password
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <button 
                                className="logout-button" 
                                onClick={handleLogout}
                                title={shouldBlockNavigation ? getBlockMessage() : 'Logout'}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </nav>
            <div className="page-with-tabs">
                <Outlet />
            </div>
            <InfoDialog
                isOpen={isDialogOpen}
                onClose={hideDialog}
                title={dialogTitle}
                message={dialogMessage}
                onContinue={shouldBlockNavigation ? handleContinueAnyway : undefined}
            />
            {selectedConverter && (
                <ConverterModal
                    isOpen={isConverterModalOpen}
                    onClose={handleCloseConverterModal}
                    converterType={selectedConverter}
                />
            )}
        </>
    );
};

export default MainNavigation; 